import type { PlannerProject } from "../types";
import {
  productFamilySingular,
  productFamilyTitles,
  productModules,
  projectProductFamilies,
  type ProductFamily
} from "../domain/productWorkflow";
import "./ProductChooser.css";

type ProductCardKind = ProductFamily;

export function ProductIllustration({ family }: { family: ProductCardKind }) {
  return <svg viewBox="0 0 220 170" aria-hidden="true" className="product-illustration">
    <ellipse cx="116" cy="154" rx="74" ry="8" fill="#5c463c" opacity=".09" />
    <path d={family === "chest" ? "M45 63 64 53H177L158 63Z" : "M45 27 64 17H177L158 27Z"} fill="#e7d9c9" />
    <path d={family === "chest" ? "M158 63 177 53V141L158 152Z" : "M158 27 177 17V141L158 152Z"} fill="#a08b73" />
    <path d={family === "chest" ? "M45 63H158V152H45Z" : "M45 27H158V152H45Z"} fill="#c5b095" stroke="#ac967a" />
    {family === "wardrobe" ? <>
      <path d="M49 31H100V148H49ZM104 31H154V148H104Z" fill="#eee4d7" />
      <path d="M93 81V108M112 81V108" stroke="#806c56" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M49 56H100M104 56H154" stroke="#d0beaa" />
    </> : family === "shelving" ? <>
      <path d="M49 31H154V148H49Z" fill="#f4eee6" />
      <path d="M49 61H154M49 91H154M49 122H154M86 31V61M118 31V61M95 61V91M125 61V91M85 91V122M120 91V122" stroke="#b19b80" strokeWidth="4" />
      <path d="M49 124H100V148H49ZM104 124H154V148H104Z" fill="#c4ae91" />
      <path d="M56 77V88M61 73V88M66 75V88" stroke="#9c8871" strokeWidth="4" />
      <path d="M126 59H140L138 48H128Z" fill="#bbb6a6" />
    </> : <>
      <path d="M49 67H154V148H49Z" fill="#eee4d7" />
      <path d="M49 93H154M49 120H154M102 67V148" stroke="#b19b80" strokeWidth="3" />
      <path d="M70 80H84M119 80H133M70 107H84M119 107H133M70 134H84M119 134H133" stroke="#806c56" strokeWidth="2.4" strokeLinecap="round" />
    </>}
  </svg>;
}

export function ProductChooser({ project, onChoose }: {
  project: PlannerProject; onChoose: (family: ProductFamily) => void;
}) {
  const projectFamilies = projectProductFamilies(project);
  const currentFamily = projectFamilies.length === 1 ? projectFamilies[0] : null;
  return <section className="product-chooser" aria-label="Выбор типа изделия">
    <div className="product-chooser__heading">
      <span className="eyebrow">Шаг 2 · Изделие</span>
      <h3>{currentFamily ? `Ваш ${productFamilySingular[currentFamily]}` : "Что будем собирать?"}</h3>
      <p>Один проект — одно изделие. Так размеры, цена и документы сразу соответствуют одному заказу в производстве.</p>
    </div>
    {(["wardrobe", "shelving", "chest"] as const).map(family => {
      const count = productModules(project, family).length;
      const isCurrent = currentFamily === family;
      const startsNewOrder = Boolean(project.modules.length && !projectFamilies.includes(family));
      return <button type="button" className={`product-choice${isCurrent ? " is-current" : ""}`} key={family} onClick={() => onChoose(family)}>
        <span className="product-choice__status">{isCurrent ? "Текущий заказ" : startsNewOrder ? "Новый заказ" : "Выбрать"}</span>
        <ProductIllustration family={family} />
        <span className="product-choice__copy">
          <strong>{productFamilyTitles[family]}</strong>
          <span>{family === "wardrobe" ? "Корпусы, наполнение, двери" : family === "shelving" ? "Сетка, ячейки, полки и ящики" : "Колонки ящиков, ножки или цоколь"}</span>
          <small>{count ? "Продолжить настройку →" : startsNewOrder ? "Начать отдельный проект →" : "Начать сборку →"}</small>
        </span>
      </button>;
    })}
    <p className="product-chooser__note">Комната задаётся один раз для изделия. Для другого изделия начните новый проект — текущий можно вернуть кнопкой «Отменить».</p>
  </section>;
}

export function ProductGuide({ family, initiallyOpen = false }: { family: ProductFamily | null; initiallyOpen?: boolean }) {
  return <details className="product-guide" key={`${family}-${initiallyOpen}`} open={initiallyOpen || undefined}>
    <summary>Как собрать {family ? productFamilySingular[family] : "изделие"}?</summary>
    {!family ? <ol>
      <li>Выберите «Шкафы», «Стеллажи» или «Комоды» на стартовом экране.</li>
      <li>Пройдите шаги выбранного изделия. Вернуться и изменить параметры можно в любой момент.</li>
      <li>Для другого типа изделия начните новый проект. Прежний вариант можно сразу вернуть кнопкой «Отменить».</li>
      <li>Комната, стоимость и документы относятся только к текущему изделию.</li>
    </ol> : family === "chest" ? <ol>
      <li>Возьмите готовый комод из витрины ниже или соберите новый.</li>
      <li>Задайте ширину, высоту и глубину, число колонок и ящиков в каждой колонке, ножки или цоколь.</li>
      <li>Выберите материал, фасады и ручки — они общие для всех ящиков. Нажатие на ящик в 3D выдвигает его.</li>
      <li>В «Итоге» проверьте проект и скачайте PDF с расчётом. Отправьте заявку дизайнеру — техническая карта для мастерской уйдёт вместе с ней.</li>
    </ol> : family === "shelving" ? <ol>
      <li>Возьмите готовую модель из витрины ниже или нажмите «Пустой стеллаж». Задайте габариты, сетку колонок и рядов и то, как стеллаж стоит на полу.</li>
      <li>Нажмите «Настроить ячейки» или саму ячейку в 3D. В панели ячейки: дверца или ящики, полки и перегородки, ширина ячейки, подсветка, объединение с соседними.</li>
      <li>Стойки и полки сетки можно тянуть мышью прямо в 3D. Щелчок по стойке открывает «Поворот стойки» — наклон на 10° влево или вправо.</li>
      <li>Выберите материал и отделку фасадов. В 3D нажмите на ящик или дверцу, чтобы открыть их.</li>
      <li>В «Итоге» проверьте проект и скачайте PDF с расчётом. Отправьте заявку дизайнеру — техническая карта для мастерской уйдёт вместе с ней.</li>
    </ol> : <ol>
      <li>Добавьте корпус и задайте его размеры. Выше 2750 мм сверху сама появляется антресоль, «Антресоль до потолка» доводит шкаф до потолка. Открытые полки сбоку — «Добавить торцевые полки».</li>
      <li>Выберите материал. В «Наполнении» добавьте полки, штанги, ящики и подсветку.</li>
      <li>Настройте фасады и ручки. Нажатие на дверь в 3D открывает её.</li>
      <li>В «Итоге» проверьте проект и скачайте PDF с расчётом. Отправьте заявку дизайнеру — техническая карта для мастерской уйдёт вместе с ней.</li>
    </ol>}
    <p>Вращайте сцену за свободное место, приближайте колесом мыши к тому месту, где курсор, сдвигайте вид правой кнопкой. Изменение можно вернуть кнопкой «Отменить».</p>
  </details>;
}
